// FILE: /src/Components/Apps/AppCard.js

import React from "react";
import { Button } from "antd";
import { ArrowRightOutlined, ClockCircleOutlined } from "@ant-design/icons";
import AppCreditBadge from "./AppCreditBadge";

const statusConfig = {
  active: {
    label: "Available",
    pillClass: "bg-emerald-50 text-emerald-700 border-emerald-100",
  },
  coming_soon: {
    label: "Soon",
    pillClass: "bg-slate-50 text-slate-500 border-slate-200",
  },
  disabled: {
    label: "Disabled",
    pillClass: "bg-gray-50 text-gray-400 border-gray-200",
  },
};

const categoryThemeMap = {
  Image: {
    iconWrap: "bg-indigo-50 text-indigo-600 ring-indigo-100",
    accent: "bg-indigo-500",
    glow: "from-indigo-500/8 via-purple-500/5 to-transparent",
  },
  Video: {
    iconWrap: "bg-rose-50 text-rose-600 ring-rose-100",
    accent: "bg-rose-500",
    glow: "from-rose-500/8 via-pink-500/5 to-transparent",
  },
  Copywriting: {
    iconWrap: "bg-cyan-50 text-cyan-600 ring-cyan-100",
    accent: "bg-cyan-500",
    glow: "from-cyan-500/8 via-sky-500/5 to-transparent",
  },
  "Social Content": {
    iconWrap: "bg-violet-50 text-violet-600 ring-violet-100",
    accent: "bg-violet-500",
    glow: "from-violet-500/8 via-purple-500/5 to-transparent",
  },
  Brand: {
    iconWrap: "bg-amber-50 text-amber-600 ring-amber-100",
    accent: "bg-amber-500",
    glow: "from-amber-500/8 via-orange-500/5 to-transparent",
  },
  Workflow: {
    iconWrap: "bg-emerald-50 text-emerald-600 ring-emerald-100",
    accent: "bg-emerald-500",
    glow: "from-emerald-500/8 via-teal-500/5 to-transparent",
  },
};

const fallbackTheme = {
  iconWrap: "bg-slate-50 text-slate-600 ring-slate-100",
  accent: "bg-slate-500",
  glow: "from-slate-500/8 via-slate-400/5 to-transparent",
};

const AppCard = ({ app, credits, onOpen }) => {
  const isActive = app.status === "active";
  const status = statusConfig[app.status] || statusConfig.disabled;
  const theme = categoryThemeMap[app.category] || fallbackTheme;

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border bg-white transition-all duration-300 ${
        isActive
          ? "border-gray-200/80 shadow-sm hover:border-gray-300 hover:shadow-lg hover:-translate-y-0.5"
          : "border-gray-100 shadow-sm opacity-75"
      }`}
    >
      <div
        className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${theme.glow} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
      />

      <div className={`absolute left-0 top-5 h-8 w-1 rounded-r-full ${theme.accent} opacity-70`} />

      <div className="relative p-4 min-h-[168px] flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center ring-1 ${theme.iconWrap}`}
          >
            <i
              className={`${
                app.icon || "fa-solid fa-wand-magic-sparkles"
              } text-base`}
            ></i>
          </div>

          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${status.pillClass}`}
          >
            {status.label}
          </span>
        </div>

        <div className="mt-3 flex-grow">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 mb-1">
            {app.category || "App"}
          </p>

          <h3 className="text-[15px] font-extrabold text-gray-900 tracking-tight leading-snug mb-1.5">
            {app.name}
          </h3>

          <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">
            {app.description}
          </p>
        </div>

        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
          <AppCreditBadge cost={app.cost} credits={credits} size="small" />

          {isActive ? (
            <Button
              type="primary"
              size="small"
              className="rounded-lg shadow-sm px-3"
              onClick={() => onOpen?.(app)}
            >
              Open <ArrowRightOutlined />
            </Button>
          ) : (
            <Button
              size="small"
              disabled
              className="rounded-lg px-3"
              icon={<ClockCircleOutlined />}
            >
              Soon
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppCard;